import { Link } from "react-router-dom";
import { Overline, Reveal } from "@/components/Reveal";
import { CANDIDATE } from "@/data/campaign";

const SECTIONS = [
  {
    title: "Who we are",
    body: [
      `This website is operated by the Campaign to Elect ${CANDIDATE.name} for Ward 11 City Councillor, Mississauga (“the campaign”, “we”, “us”). You can reach us at any time at ${CANDIDATE.email} or ${CANDIDATE.phone}.`,
      "This policy explains what personal information we collect through this website, why we collect it, how it is stored, and the choices you have.",
    ],
  },
  {
    title: "What we collect",
    list: [
      "Volunteer form: name, email, phone number, address, neighbourhood, availability, and the activities you’re interested in.",
      "Lawn sign requests: name, email, and street address so we can deliver and pick up your sign.",
      "Campaign updates: name and email address.",
      "Share your priority: name, email, neighbourhood, topic, and your message.",
      "Contact form: your name, email, and message.",
      "Donations: your legal first and last name, email, phone number, street address, city, province, postal code, contribution amount, and your attestation that you are an Ontario resident contributing your own funds, as required by Ontario municipal election law.",
    ],
  },
  {
    title: "How donations are processed",
    body: [
      "Online contributions are processed securely by Stripe. Your payment card details are entered directly into Stripe’s checkout and are never seen, transmitted, or stored by our servers.",
      "Under the Ontario Municipal Elections Act, 1996, contributions may only be made by individuals who are residents of Ontario, using their own funds, to a maximum of $1,200 per person to a single candidate in an election period. Contributor information (name, address, and amount) is collected and retained as required for campaign finance record-keeping and any required reporting to the City of Mississauga.",
      "Contributions to municipal campaigns are not eligible for income tax receipts.",
    ],
  },
  {
    title: "How we use your information",
    list: [
      "To respond to your messages, questions, and requests.",
      "To coordinate volunteering and lawn sign delivery and pickup.",
      "To send campaign updates you asked to receive (you can unsubscribe at any time by replying to any email or contacting us).",
      "To send a confirmation or receipt email when you volunteer, subscribe, submit a priority, request a sign, or donate.",
      "To comply with Ontario municipal election contribution rules and record-keeping obligations.",
    ],
    body: ["We do not sell, rent, or trade your personal information to anyone, ever."],
  },
  {
    title: "Where your data is stored",
    body: [
      "Form submissions and contribution records are stored in a secure, access-controlled database hosted in Canada (ca-central-1). Access is limited to authorized campaign team members.",
      "Emails are sent through our email service provider (Resend). Bot protection on our forms is provided by Cloudflare Turnstile, which may process a small amount of device and interaction data to verify you are human. Payment processing is handled by Stripe. These providers process data only as needed to deliver their service to us.",
    ],
  },
  {
    title: "Cookies",
    body: [
      "This website uses a single essential cookie to keep authorized campaign administrators signed in. We do not use advertising or tracking cookies, and we do not run third-party analytics that profile visitors.",
    ],
  },
  {
    title: "How long we keep it",
    body: [
      "Volunteer, lawn sign, updates, priority, and contact records are kept for the duration of the campaign and deleted within 12 months after election day, unless you ask us to remove them sooner.",
      "Contribution records are retained for the period required by Ontario municipal election law and the City of Mississauga’s campaign finance rules.",
    ],
  },
  {
    title: "Your choices",
    body: [
      `You can ask us at any time to see, correct, or delete the personal information we hold about you, or to stop sending you updates. Email ${CANDIDATE.email} or call ${CANDIDATE.phone} and we’ll take care of it promptly.`,
    ],
  },
  {
    title: "Changes to this policy",
    body: [
      "If we change how we handle personal information, we will update this page and revise the date below.",
    ],
  },
];

const PrivacyPolicy = () => (
  <main data-testid="privacy-page" className="pt-28 lg:pt-32">
    <section className="mx-auto max-w-4xl px-5 pb-24 sm:px-8">
      <Reveal>
        <Overline>Privacy</Overline>
        <h1 className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
          Your privacy, <span className="italic text-terra-500">respected</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/75 sm:text-lg">
          Neighbours trusting us with their information is something we take seriously. Here is exactly what this
          campaign collects, why, and how you stay in control of it.
        </p>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-ink/50" data-testid="privacy-updated">
          Last updated · July 2026
        </p>
      </Reveal>

      <div className="mt-12 border-t border-pine-700/15">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={Math.min(i * 0.04, 0.2)}>
            <section data-testid={`privacy-section-${i}`} className="border-b border-pine-700/15 py-8">
              <h2 className="font-serif text-xl font-semibold tracking-tight text-ink sm:text-2xl">{s.title}</h2>
              {s.body?.map((p) => (
                <p key={p.slice(0, 40)} className="mt-4 text-base leading-relaxed text-ink/75">
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink/75">
                  {s.list.map((item) => (
                    <li key={item.slice(0, 40)}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <p className="mt-10 text-base leading-relaxed text-ink/75">
          Questions about this policy?{" "}
          <Link to="/contact" data-testid="privacy-contact-link" className="text-terra-500 underline underline-offset-4 transition-colors hover:text-terra-600">
            Contact the campaign
          </Link>{" "}
          and we’ll be glad to help.
        </p>
      </Reveal>
    </section>
  </main>
);

export default PrivacyPolicy;
